'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: true,
    marketing: false,
    functional: true,
  });

  useEffect(() => {
    const consent = localStorage.getItem('dialforflight_cookie_consent');
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem('dialforflight_cookie_consent', JSON.stringify({
      preferences: prefs,
      timestamp: new Date().toISOString(),
    }));
    setIsVisible(false);
    setShowManage(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const handleDeclineAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    setPreferences(onlyNecessary);
    saveConsent(onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isVisible) return null;

  const cookieTypes = [
    {
      key: 'necessary' as keyof CookiePreferences,
      label: 'Strictly Necessary',
      description: 'Required for the website to function. Cannot be disabled.',
      locked: true,
    },
    {
      key: 'functional' as keyof CookiePreferences,
      label: 'Functional',
      description: 'Enable personalised features like saved searches and preferences.',
      locked: false,
    },
    {
      key: 'analytics' as keyof CookiePreferences,
      label: 'Analytics',
      description: 'Help us understand how visitors use our site to improve your experience.',
      locked: false,
    },
    {
      key: 'marketing' as keyof CookiePreferences,
      label: 'Marketing',
      description: 'Used to show you relevant travel deals and personalised advertisements.',
      locked: false,
    },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-charcoal text-white rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
          {!showManage ? (
            /* Main Banner */
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="ShieldCheckIcon" size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-white text-base mb-1">We use cookies 🍪</h3>
                  <p className="text-slate text-sm leading-relaxed mb-4">
                    We use cookies to enhance your browsing experience, provide personalised travel deals, and analyse our traffic.
                    By clicking &quot;Accept All&quot;, you consent to our use of cookies.{' '}
                    <Link href="/terms-and-conditions#cookies" className="text-primary hover:underline">Learn more</Link>
                    {' '}or view our{' '}
                    <Link href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={handleDeclineAll}
                      className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      Decline All
                    </button>
                    <button
                      onClick={() => setShowManage(true)}
                      className="px-5 py-2.5 border border-white/20 hover:border-white/40 text-slate hover:text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Manage Preferences
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleDeclineAll}
                  className="w-8 h-8 flex items-center justify-center text-slate hover:text-white transition-colors flex-shrink-0"
                  aria-label="Close cookie banner"
                >
                  <Icon name="XMarkIcon" size={18} />
                </button>
              </div>
            </div>
          ) : (
            /* Manage Preferences Panel */
            <div className="p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowManage(false)}
                    className="w-8 h-8 flex items-center justify-center text-slate hover:text-white transition-colors"
                    aria-label="Back"
                  >
                    <Icon name="ArrowLeftIcon" size={18} />
                  </button>
                  <h3 className="font-heading font-bold text-white text-base">Manage Cookie Preferences</h3>
                </div>
                <button
                  onClick={handleDeclineAll}
                  className="w-8 h-8 flex items-center justify-center text-slate hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <Icon name="XMarkIcon" size={18} />
                </button>
              </div>

              <div className="space-y-3 mb-5">
                {cookieTypes.map((cookie) => (
                  <div key={cookie.key} className="flex items-start justify-between gap-4 p-3 bg-white/5 rounded-xl">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-white text-sm">{cookie.label}</span>
                        {cookie.locked && (
                          <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Required</span>
                        )}
                      </div>
                      <p className="text-slate text-xs leading-relaxed">{cookie.description}</p>
                    </div>
                    <button
                      onClick={() => togglePreference(cookie.key)}
                      disabled={cookie.locked}
                      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 mt-0.5 ${
                        preferences[cookie.key]
                          ? 'bg-primary' :'bg-white/20'
                      } ${cookie.locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                      aria-label={`Toggle ${cookie.label} cookies`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          preferences[cookie.key] ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  Save My Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  Accept All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
